import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { showSearch, verifyPayment } from "../../Services/services";
import {
  CHEVRON_LEFT,
  EMPTY_SEARCH_2,
  SEARCH_INPUT_ICON,
  SUCCESS_IMG,
} from "../../assets";
import TableComponent from "../../shared/components/table";
import {
  SEARCH_HISTORY_COLUMN,
  SEARCH_RESULT_COLUMN,
} from "../../shared/components/table/columns";

import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import ButtonV2 from "../../shared/components/buttonV2";
import ModalV2 from "../../shared/components/modalV2";
import { useAppDispatch } from "../../hooks";
import { setReference } from "../../states/slices/globalReducer";

function SearchResult() {
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingresult, setLoadingresult] = useState(false);
  const [currentpage, setCurrentpage] = useState(0);
  const [preloader, setPreloader] = useState(false);
  const [success, setSuccess] = useState(false);
  const dispatch = useAppDispatch();
  const [searchResult, setSearchResult] = useState<any>([]);
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("reference") || "";

  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const selectedButton = pagesRefs.current[currentpage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentpage]);

  const handleVerifyPayment = async () => {
    setPreloader(true);

    const payload = {
      reference: reference,
    };

    try {
      const response = await verifyPayment(payload);
      if (response) {
        setSuccess(true);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setPreloader(false);
    }
  };

  const handleShowResult = async () => {
    setLoadingresult(true);
    const payload = {
      reference: reference,
    };
    try {
      const response = await showSearch(payload);
      if (response) {
        setSuccess(false);
        setSearchResult(response?.data);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoadingresult(false);
    }
  };

  useEffect(() => {
    if (reference) {
      handleVerifyPayment();
      dispatch(setReference(reference));
    }
  }, []);

  return (
    <div className="p-3 sm:p-5">
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-xl sm:text-2xl">
          Search Result
        </span>
        <span className="text-primary-50 font-medium text-sm">
          View your search results here
        </span>
      </div>

      <div className="w-full mt-5 border border-borderColor py-4 rounded-xl">
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 gap-3">
          <span className="text-[#2A2E34] font-semibold text-[16px]">
            Results
          </span>
          <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
            <img
              src={SEARCH_INPUT_ICON}
              className="w-[20px] h-[20px] flex-shrink-0"
              alt=""
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent placeholder:text-sm outline-none min-w-0"
              placeholder="Search property title,location"
            />
          </div>
        </div>

        {searchResult?.length > 0 ? (
          <div className="mt-2">
            <div className="overflow-x-auto">
              <TableComponent
                DATA={searchResult}
                //@ts-ignore
                COLUMNS={SEARCH_RESULT_COLUMN}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full px-4">
            <img
              src={EMPTY_SEARCH_2}
              className="w-[120px] h-[100px] sm:w-[147.69px] sm:h-[120px]"
              alt=""
            />
            <span className="text-lg sm:text-xl font-bold text-customDark text-center">
              No Matching Records Found
            </span>
            <span className="text-sm font-medium text-primary-50 max-w-md text-center">
              We couldn't find any records that match your search. Try refining
              your details or start a new search below.
            </span>
          </div>
        )}
      </div>

      {preloader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-6 px-6 sm:px-8 py-6 rounded-2xl shadow-2xl bg-white/10 border border-white/20 backdrop-blur-md mx-4">
            <div className="relative w-12 sm:w-14 h-12 sm:h-14">
              <div className="absolute inset-0 rounded-full border-4 border-t-transparent border-green-400 animate-spin" />
              <div className="absolute inset-0 rounded-full border-4 border-white opacity-10" />
            </div>
            <p className="text-base sm:text-lg font-semibold text-white tracking-wide animate-pulse text-center">
              Verifying Payment...
            </p>
          </div>
        </div>
      )}

      <ModalV2
        isOpen={success}
        isClose={() => setSuccess(false)}
        maxWidth={"w-[90vw] max-w-[400px]"}
        edges={"rounded-xl"}
      >
        <div className="flex flex-col items-center gap-y-4 p-4 sm:p-6">
          <img
            src={SUCCESS_IMG}
            className="w-full h-[120px] sm:h-[160px] object-contain"
            alt=""
          />
          <div className="flex flex-col gap-y-3 text-center">
            <span className="text-[#303237] font-medium text-lg sm:text-xl">
              Payment Successful
            </span>
            <span className="text-sm font-medium text-[#2A2E34] leading-relaxed">
              Your payment has been processed successfully. You can now view the
              search results for your property query. Click the button below to
              retrieve your search results.
            </span>
          </div>

          <ButtonV2
            title={"View Result"}
            loading={loadingresult}
            loaderColor="#ffffff"
            btnStyle={
              "w-full bg-globalGold flex items-center justify-center rounded-xl p-3 mt-4"
            }
            textStyle={"text-white font-normal"}
            handleClick={handleShowResult}
          />
        </div>
      </ModalV2>
    </div>
  );
}

export default SearchResult;
